<template>
  <article
    class="c-notification-card"
    :class="{ new: isNew }"
    @click="handleClick"
    @keyup.enter="handleClick"
  >
    <div class="c-notification-card__avatar">
      <user-avatar :user="action_user" />
    </div>
    <div class="c-notification-card__content">
      <article>{{ message }}</article>
      <footer>
        <time :datetime="$dayjs(formatTime).format('YYYY-MM-DD')">{{
          formatTime
        }}</time>
      </footer>
    </div>
    <div class="c-notification-card__actions">
      <slot name="actions"></slot>
    </div>
  </article>
</template>

<script>
const UserAvatar = () => import("@/components/UserAvatar");
export default {
  props: {
    users: {
      type: [Array],
      default: () => [],
    },
    message: {
      type: String,
      default: "",
    },
    avatar: {
      type: String,
      default: "",
    },
    status: {
      type: String,
      default: "",
    },
    object: {
      type: String,
      default: "",
    },
    created_time: {
      type: String,
      default: "",
    },
    self: {
      type: String,
      default: "",
    },
    action_user: {
      type: Object,
      default: () => {},
    },
    resource: {
      type: Object,
      default: () => {},
    },
    admin: {
      type: Number,
      default: 1,
    },
  },
  methods: {
    handleClick() {
      /**
       * If it is an admin notification, go to the edit screen
       * If it is not, go to the actual resource
       * If we are an FCM, just go to the notifications page
       */

      if (this.self.substring(0, 4) == "fcm/") {
        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            this.$router.push({ path: "/notifications" });
          });
        return;
      }

      const type = this.resource.self;

      let url;

      if (type) {
        if (type.includes("news/article")) {
          url =
            this.admin === 1
              ? `/admin/news/${this.resource.id}/edit`
              : this.resource.self;
        }
        if (type.includes("schedule/event")) {
          url =
            this.admin === 1
              ? `/admin/events/edit/${this.resource.id}`
              : this.resource.self;
        }
        if (type.includes("content/page")) {
          url =
            this.admin === 1
              ? `/admin/content/pages/${this.resource.id}/edit`
              : `/content/page/${this.resource.page_url}`;
        }

        if (type.includes("system/status")) {
          url = `/system-status/${this.resource.id}/status`;
        }

        if (type.includes("collaboration/question") && !this.resource.idea_id) {
          url = `/collaboration/question/${this.resource.id}`;
        }

        // surveys
        if (type.includes("surveymanagement/survey")) {
          url = `/survey${this.resource.survey_url}`;
        }

        // eforms
        if (type.includes("eformmanagement/eform")) {
          url = `/forms${this.resource.eform_url}`;
        }

        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            this.$router.push({ path: url });
          });
      }

      //collaboration
      if (this.resource.question_id && this.resource.idea_id) {
        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            this.$router.push({
              path: `/collaboration/question/${this.resource.question_id}/idea/${this.resource.idea_id}`,
            });
          });
        return;
      }
      if (this.resource.question_id && this.resource.id) {
        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            this.$router.push({
              path: `/collaboration/question/${this.resource.question_id}/idea/${this.resource.id}`,
            });
          });
        return;
      }

      if (this.resource.question_id) {
        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            this.$router.push({
              path: `/collaboration/question/${this.resource.question_id}/`,
            });
          });
        return;
      }

      /**
       * If we are a form or policy, we need to do certain things
       * If the status is pending, we need to go to the approve screen if we are approving
       * If we are rejected, we need to go to the edit screen
       * If we are approved, we need to go to the view screen
       */
      if (this.resource.form_id || this.resource.policy_id) {
        const docId = this.resource.form_id
          ? this.resource.form_id
          : this.resource.policy_id;
        const docType = this.resource.form_id ? "forms" : "policies";
        const { status, id } = this.resource;

        this.$store
          .dispatch("userNotifications/read", this.self)
          .then((resp) => {
            if (status === "pending") {
              this.$router.push({
                path: `/admin/documents/${docType}/${docId}/version/${id}`,
              });
            }

            if (status === "rejected") {
              this.$router.push({
                path: `/admin/documents/${docType}/${docId}/version/${id}/edit`,
              });
            }

            if (status === "approved") {
              this.$router.push({
                path: `/documents/${docType}/${docId}`,
              });
            }
          });
      }
    },
  },
  computed: {
    userAvatar() {
      return "https://www.gravatar.com/avatar/sdfdsfdsfdsfsd?f=y&d=mp";
    },
    isNew() {
      return this.status === "unread";
    },
    /**
     * Format the text based on the users and actions passed
     */
    formatText() {
      //turn [1,2] into 1 and 2, [1,2,3] into 1, 2 and 3
      const usernames = this.users.map((user) => user.name);
      let userString = usernames.join("</strong>, <strong>");
      userString = userString.replace(/\, ([^\,]*)$/, " and $1");
      return `<strong>${userString}</strong> ${this.action} your ${this.object}`;
    },
    formatTime() {
      // return 'time goes here';
      return this.$dayjs().to(this.created_time);
    },
  },
  name: "NotificationCard",
  components: {
    UserAvatar,
  },
};
</script>

<style scoped lang="scss">
.c-notification-card {
  padding: 20px;
  display: flex;
  text-align: left;
  cursor: pointer;
  background: $colour-nhs-white;
  position: relative;
  transition: all ease-in-out 0.2s;

  &:hover {
    // background: darken($colour-nhs-white,5%);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.11);
    z-index: 99;
  }

  &:hover & {
    &__actions {
      opacity: 1;
    }
  }

  &.new {
    background: #f3f6f6;
    border-left: 6px solid #005eb8;
  }

  &__avatar {
    margin-right: 20px;
    width: 51px;
    max-height: 51px;
    // flex: 1;
    border-radius: 100%;
    overflow: hidden;
    img {
      border-radius: 100%;
      max-width: 51px;
      max-height: 51px;
    }
  }

  &__actions {
    flex: 1;
    opacity: 1;
    justify-self: flex-end;
    transition: all ease-in-out 0.2s;

    @media (hover: hover) {
      opacity: 0;
    }
  }

  &__content {
    font-size: 14px;
    flex: 12;
    main {
      color: $colour-nhs-black;
    }
    time {
      color: $colour-nhs-dark-grey;
    }
  }
}
</style>
<style lang="scss">
.c-notification-card__content strong {
  color: $colour-nhs-core-blue;
  font-family: $font-frutiger-bold;
}
</style>
